import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Star, Loader2, ArrowLeft, Mail, Lock, User, Phone, MapPin, Briefcase } from 'lucide-react';
import { jsPDF } from 'jspdf';

export default function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    gender: '',
    profession: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenderChange = (value) => {
    setFormData({ ...formData, gender: value });
  };

  const generateMatricule = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000); // 4 digit random
    return `M-${year}-${random}`;
  };

  const generateMembershipCard = (user) => {
    const doc = new jsPDF();

    // Set background color
    doc.setFillColor(13, 31, 69); // Midnight blue ish (matches theme) | Using #0d1f45 -> R13 G31 B69
    doc.rect(0, 0, 85.6, 54, 'F'); // Credit card size approx 85.6mm x 54mm

    // Add Border
    doc.setLineWidth(0.5);
    doc.setDrawColor(212, 175, 55); // Gold color
    doc.rect(2, 2, 81.6, 50);

    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("DAARA WAKANA HAQQAN", 42.8, 8, { align: 'center' });

    // Logo placeholder (Circle)
    doc.setFillColor(255, 255, 255);
    doc.circle(12, 12, 6, 'F');
    // Ideally add actual image if available in base64, simplified here
    doc.setTextColor(0, 0, 0);
    doc.text("Logo", 12, 12, { align: 'center', baseline: 'middle' });

    // Title
    doc.setTextColor(212, 175, 55); // Gold
    doc.setFontSize(10);
    doc.text("CARTE DE MEMBRE", 42.8, 15, { align: 'center' });

    // Content
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");

    const startY = 22;
    const lineHeight = 4;

    doc.text(`Matricule: ${user.matricule}`, 10, startY);
    doc.text(`Nom: ${user.lastName}`, 10, startY + lineHeight);
    doc.text(`Prénom: ${user.firstName}`, 10, startY + lineHeight * 2);
    doc.text(`Profession: ${user.profession}`, 10, startY + lineHeight * 3);
    doc.text(`Téléphone: ${user.phone}`, 10, startY + lineHeight * 4);

    // Footer
    doc.setFontSize(5);
    doc.setTextColor(200, 200, 200);
    doc.text("Cette carte est personnelle et incessible.", 42.8, 50, { align: 'center' });

    // Save
    doc.save(`carte_membre_${user.matricule}.pdf`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const matricule = generateMatricule();
      const fullName = `${formData.firstName} ${formData.lastName}`;
      const additionalData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        gender: formData.gender,
        profession: formData.profession,
        matricule: matricule,
        adhesionAmount: 1140,
        adhesionPaid: false // Assuming payment is done later or cash
      };

      await register(formData.email, formData.password, fullName, formData.phone, additionalData);

      // Generate PDF
      generateMembershipCard({ ...additionalData, phone: formData.phone });

      navigate('/pending-approval');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Cet email est déjà utilisé');
      } else {
        console.error(err);
        setError('Une erreur est survenue lors de l\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à l'accueil
        </Link>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-primary">
                <img src="/logo.jpg" alt="Logo" className="w-full h-full object-cover" />
              </div>
            </div>
            <CardTitle className="text-2xl">Inscription Membre</CardTitle>
            <CardDescription>
              Formulaire d'adhésion - Frais: 1140 FCFA
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="register-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Prénom */}
                <div className="space-y-2">
                  <Label htmlFor="firstName">Prénom</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="firstName"
                      name="firstName"
                      placeholder="Votre prénom"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Nom */}
                <div className="space-y-2">
                  <Label htmlFor="lastName">Nom</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="lastName"
                      name="lastName"
                      placeholder="Votre nom"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Sexe */}
                <div className="space-y-2">
                  <Label>Sexe</Label>
                  <Select onValueChange={handleGenderChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculin</SelectItem>
                      <SelectItem value="F">Féminin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Profession */}
                <div className="space-y-2">
                  <Label htmlFor="profession">Profession</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="profession"
                      name="profession"
                      placeholder="Votre profession"
                      value={formData.profession}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="address"
                    name="address"
                    placeholder="Votre adresse complète"
                    value={formData.address}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {/* Téléphone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+221 77 000 00 00"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10"
                    required
                    data-testid="register-phone"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email (Facultatif)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-password"
                    />
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-primary">Montant d'adhésion:</span>
                  <span className="text-xl font-bold">1140 FCFA</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Payable à l'inscription</p>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Inscription...
                  </>
                ) : (
                  'Confirmer l\'inscription'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground">
                Déjà membre ?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login">
                  Se connecter
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
